import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import cx from 'classnames';
import { useParallax } from 'react-scroll-parallax';

import { useSelector } from 'react-redux';
import Container from 'components/Container';
import SectionTitle from 'components/SectionTitle';
import SocialMediaLinks from 'components/SocialMediaLinks';
import StyledContent from 'components/StyledContent';
import Thumbnail from 'components/Thumbnail';

import { appSelectors } from 'ducks/app';
import Contacts from './components/Contacts';
import MoreExternalLink from './components/MoreExternalLink';

import styles from './styles.module.scss';

const MainArticleBlock = ({ entity, firstParagraph, type, figure }) => {
	const {
		address,
		city,
		contacts,
		federalSubject,
		title,
		thumbnailFile,
		shortText,
		jsonText
	} = entity;

	const redirectUrl = useSelector(appSelectors.selectRedirectUrl);

	const { asPath } = useRouter();

	const figureGroupParallax = useParallax({ speed: 30 });

	const thumbnailAlt = thumbnailFile?.meta?.description
		? thumbnailFile.meta.description
		: `Основное изображение для статьи ${title}`;
	const headClasses = cx(
		styles.head,
		type === 'institutes' && styles.headHasFigure
	);

	const currentCity =
		city?.title || address?.replace(/(.+)(г\.\s)([А-ЯЁа-яё\s-]+)(,.+)/, '$3');

	const location = currentCity &&
		federalSubject && {
			city: currentCity,
			federalSubject: federalSubject?.title
		};

	return (
		<>
			<Container width="medium" className={styles.primaryContainer}>
				<div className={headClasses}>
					<SectionTitle
						title={title}
						description={address}
						theme="tertiary"
						className={styles.mainTitle}
					/>
					{type === 'publications' && (
						<div className={styles.subtitle}>Советует Культура.рф</div>
					)}
					{!_(contacts).isEmpty() && (
						<Contacts contacts={contacts} location={location} />
					)}
					{type === 'publications' && shortText && (
						<div className={styles.description}>{shortText}</div>
					)}
					<SocialMediaLinks titleText={title} shortText={shortText} />
				</div>
				{thumbnailFile && (
					<div className={styles.cover}>
						<Thumbnail
							className={styles.thumbnail}
							file={thumbnailFile}
							params={{ width: 940, height: 529 }}
							alt={thumbnailAlt}
							theme="border"
							keepRatio
							showMeta
						/>
					</div>
				)}
			</Container>
			<div className={styles.WrapStyledContent}>
				<StyledContent blocks={jsonText} firstParagraph={firstParagraph} />
				<div className={styles.GroupFigure} ref={figureGroupParallax.ref}>
					{figure}
				</div>
			</div>
			{type === 'institutes' && (
				<Container width="medium" className={styles.moreContainer}>
					<MoreExternalLink url={`${redirectUrl}${asPath}`} />
				</Container>
			)}
		</>
	);
};

MainArticleBlock.propTypes = {
	entity: PropTypes.shape({
		address: PropTypes.string,
		city: PropTypes.shape({
			title: PropTypes.string
		}),
		contacts: PropTypes.shape({
			email: PropTypes.string,
			url: PropTypes.string,
			phones: PropTypes.arrayOf(
				PropTypes.shape({
					value: PropTypes.string
				})
			)
		}),
		federalSubject: PropTypes.shape({
			title: PropTypes.string
		}),
		shortText: PropTypes.string,
		title: PropTypes.string.isRequired,
		thumbnailFile: PropTypes.object,
		jsonText: PropTypes.array
	}),
	firstParagraph: PropTypes.bool,
	type: PropTypes.oneOf(['publications', 'institutes']),
	figure: PropTypes.node
};

export default MainArticleBlock;
